import React, { useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { getPath, getLanguage } from '../../util/helper.js'
import { BsCheck } from 'react-icons/bs'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Link from '../Link'
import Logo from './Logo.js'
import Globe from '../../assets/icons/globe.svg'
import Phone from '../../assets/icons/phone.svg'

const DEFAULT_LOCALE = process.env.GATSBY_DEFAULT_LOCALE

const Dropdown = ({ item, textColorClass }) => {
  const [show, setShow] = useState(false)
  const listingsPath = getPath('listings')

  return (
    <div
      className="relative cursor-pointer group"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        className={cx(
          'inline-block px-2 py-3 border-b-4 border-t-4 border-t-transparent border-b-transparent group-hover:border-b-blueLight-500',
          textColorClass
        )}
      >
        {item.itemLabel}
      </button>
      {show ? (
        <div className="absolute z-40 py-3 bg-gray-200 top-full min-w-[270px]">
          {item.dropdownItems.map(
            (
              {
                itemLink,
                itemLabel,
                apartmentFiltersCity,
                apartmentsFilterType,
                dropdownHeaderBefore,
                showDividerAfter,
              },
              index
            ) => {
              if (apartmentsFilterType && apartmentFiltersCity) {
                itemLink = `${listingsPath}?city=${apartmentFiltersCity}${
                  apartmentsFilterType !== 'sale'
                    ? `&${apartmentsFilterType}=true`
                    : ''
                }`
              } else if (apartmentsFilterType) {
                itemLink = `${listingsPath}${
                  apartmentsFilterType !== 'sale'
                    ? `?${apartmentsFilterType}=true`
                    : ''
                }`
              }

              return (
                <Fragment key={`${item.id}-${index}`}>
                  {dropdownHeaderBefore ? (
                    <h6 className="px-6 py-3 text-gray-700">
                      {dropdownHeaderBefore}
                    </h6>
                  ) : null}
                  <Link
                    className="block px-6 py-1 text-blue-500 hover:text-blueLight-800"
                    url={itemLink}
                  >
                    {itemLabel}
                  </Link>
                  {showDividerAfter ? (
                    <div className="h-px my-2 bg-gray-300" />
                  ) : null}
                </Fragment>
              )
            }
          )}
        </div>
      ) : null}
    </div>
  )
}

const Navigation = ({ items, textColorClass }) => (
  <>
    {items.map((item) =>
      item.isDropdownType ? (
        <Dropdown key={item.id} item={item} textColorClass={textColorClass} />
      ) : (
        <Link
          key={item.id}
          url={item.itemLink}
          className={cx('inline-block px-2 py-3', textColorClass)}
        >
          {item.itemLabel}
        </Link>
      )
    )}
  </>
)

const LanguageSwitcher = ({ data, textColorClass }) => {
  const { t, i18n } = useTranslation()
  const [show, setShow] = useState(false)

  return (
    <div
      className="relative cursor-pointer group"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        className={cx(
          'inline-flex px-2 py-3 items-center border-b-4 border-t-4 border-t-transparent border-b-transparent group-hover:border-b-blueLight-500',
          textColorClass
        )}
      >
        <Globe className={cx('w-5 h-5 fill-current mr-2', textColorClass)} />
        <span className="sr-only md:not-sr-only">{t('Language')}</span>
      </button>
      {show ? (
        <div className="absolute z-40 w-48 py-5 bg-gray-200 md:w-52 top-full">
          {Object.keys(data).map((lang) => {
            const active = lang === i18n.language
            return (
              <Link
                key={`${data[lang]}-${lang}`}
                className={cx(
                  'flex justify-between items-center px-6 py-1 hover:text-blueLight-800',
                  active ? 'text-blueLight-800' : 'text-blue-500'
                )}
                url={`${lang !== DEFAULT_LOCALE ? `/${lang}` : ''}/${
                  data[lang]
                }`}
                onClick={() => i18n.changeLanguage(lang)}
              >
                {getLanguage(lang)}
                {active && (
                  <BsCheck className="w-5 h-5 fill-current text-blueLight-500" />
                )}
              </Link>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

const SiteHeaderDesktop = ({ data, navConfig, isLoggedIn }) => {
  const {
    disableLogoLinks,
    textColor,
    isTransparent,
    localeSlugs,
    hideNavbarItems,
  } = navConfig

  const COUNTRY = process.env.GATSBY_COUNTRY

  const textColorClass = textColor === 'blue' ? 'text-blue-500' : 'text-white'
  const logo = (
    <Logo
      navConfig={navConfig}
      blackLogo={data.logoBlack}
      lightLogo={data.logo}
      alt="Blok"
    />
  )

  return (
    <div
      className={cx(
        'h-[72px] xl:h-[104px] flex w-full',
        hideNavbarItems ? 'block' : 'hidden xl:block',
        {
          'absolute z-30 bg-transparent': isTransparent !== false,
        }
      )}
    >
      <div className="container flex items-center py-6">
        <div
          className={cx(
            'flex-grow-0 flex-shrink-0 mr-4 mb-[6px]',
            COUNTRY.includes('LEAD') ? 'mb-[8px]' : ''
          )}
          style={{ flexBasis: COUNTRY.includes('LEAD') ? '170px' : '80px' }}
        >
          {disableLogoLinks ? (
            <>{logo}</>
          ) : (
            <Link url={getPath('/')}>{logo}</Link>
          )}
        </div>
        {!hideNavbarItems && (
          <div className="flex items-center flex-initial">
            <Navigation
              items={data.leftNavigation}
              textColorClass={textColorClass}
            />
          </div>
        )}
        <div
          className={cx(
            'flex items-center flex-initial',
            hideNavbarItems ? 'w-full justify-between' : 'ml-auto'
          )}
        >
          {!hideNavbarItems && (
            <Navigation
              items={data.rightNavigation}
              textColorClass={textColorClass}
            />
          )}
          {data?.displayLanguageSwitcher ? (
            <LanguageSwitcher
              data={localeSlugs}
              textColorClass={textColorClass}
            />
          ) : null}
          {!hideNavbarItems &&
          !data.hideLoginButton &&
          data.appLoginButtonLabel &&
          data.appLoggedInButtonLabel ? (
            <a
              href={
                isLoggedIn
                  ? data.appLoggedInButtonLink
                  : data.appLoginButtonLink
              }
              className="ml-4 btn btn-white btn-sm"
            >
              {isLoggedIn
                ? data.appLoggedInButtonLabel
                : data.appLoginButtonLabel}
            </a>
          ) : null}
          {!hideNavbarItems &&
          data.displayStartSellingButton &&
          data.appStartSellingButtonLabel ? (
            <a
              href={data.appStartSellingButtonLink}
              className={cx(
                'ml-4 btn btn-sm',
                isTransparent ? 'btn-white' : 'btn-blue'
              )}
            >
              {data.appStartSellingButtonLabel}
            </a>
          ) : null}
          {hideNavbarItems && data.phoneNumber && (
            <a
              href={`tel:${data.phoneNumber}`}
              className={cx('flex gap-1 text-right w-full justify-end', textColorClass)}
            >
              <div className="flex flex-col items-end justify-center lg:justify-end gap-1.5">
                {data.phoneTitle ? (
                  <span className="text-[10px] font-bold tracking-widest uppercase lg:leading-none">
                    {data.phoneTitle}
                  </span>
                ) : null}
                <span className="text-xl font-bold leading-none sr-only lg:not-sr-only">
                  {data.phoneNumber}
                </span>
              </div>
              <Phone className="w-10 fill-current h-11" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

SiteHeaderDesktop.propTypes = {
  data: PropTypes.object.isRequired,
  navConfig: PropTypes.object.isRequired,
  isLoggedIn: PropTypes.bool,
}

LanguageSwitcher.propTypes = {
  data: PropTypes.object.isRequired,
  textColorClass: PropTypes.string,
}

Navigation.propTypes = {
  items: PropTypes.array.isRequired,
  textColorClass: PropTypes.string,
}

Dropdown.propTypes = {
  item: PropTypes.object.isRequired,
  textColorClass: PropTypes.string,
}

export default SiteHeaderDesktop
